import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, CircularProgress, InputAdornment, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import palette from '../../../theme/palette'
import { C, Card, Reveal, SectionHead, onColor, textOn } from './aboutUi'

// ─────────────────────────────────────────────────────────────────────────────
// About PACE → Signals: every signal the served PACE model reads (from PACE, so the count
// and the list always match production), grouped into families; and, for a merchant, which
// of them its settings use for ranking: used / fallback only / not used.
// Shared by the admin page (merchant picker) and the merchant panel (own merchant only).
// ─────────────────────────────────────────────────────────────────────────────

const FAM = {
  track: { short: 'Track record', color: C.primary },
  route: { short: 'Route', color: C.info },
  merchant: { short: 'Merchant & buyers', color: C.violet },
  profile: { short: 'Courier profile', color: C.teal },
  lane: { short: 'Lane', color: C.warning },
  order: { short: 'Order', color: C.error },
  calendar: { short: 'Calendar', color: C.orange },
  warn: { short: 'Early warnings', color: palette.grey[700] },
}
const FAM_TEXT = {
  track: 'How each courier has actually performed recently: overall, and by drop state, drop city, COD vs prepaid and parcel weight.',
  route: 'Where the parcel starts and ends, and what kind of journey that is for each courier.',
  merchant: "How this merchant's own shipments behave: overall, with each courier, in each buyer region, and COD vs prepaid.",
  profile: 'Who the courier is and what it promises: service type, delivery attempts, rate-card price and promised days.',
  lane: 'How all couriers together have done between the pickup state and the drop state.',
  order: 'What is being shipped and how it was bought: payment, value, weight, size, category, channel.',
  calendar: 'When the order was placed, and how close it is to festivals and sales that slow the network down.',
  warn: 'Signs that a courier is starting to struggle: NDR calls and weight disputes in the last 30 days.',
}
const PRED = [
  { k: 'delivery', name: 'Will it deliver?', abbr: 'Del' },
  { k: 'rto', name: 'Will it come back?', abbr: 'RTO' },
  { k: 'first_attempt', name: 'First attempt on time?', abbr: '1st' },
  { k: 'time', name: 'How long will it take?', abbr: 'Time' },
]
const STATUS = {
  used: { label: 'Used in ranking', color: C.success },
  fallback: { label: 'Fallback only', color: C.warning },
  unused: { label: 'Not used with these settings', color: palette.grey[500] },
}
const SOURCE = {
  known: { label: 'Known', color: C.info, tip: 'Exact facts: the order, the route, the courier setup, the rate-card price, your settings.' },
  measured: { label: 'Measured', color: C.teal, tip: 'Counted from real past shipments.' },
  predicted: { label: 'Predicted', color: C.violet, tip: "Output of PACE's prediction models." },
}
const PART_COLOR = { delivery: C.success, fasr: C.info, tat: C.warning, speed: C.violet, cost: C.error }
const KNOB_GROUP = { cost: 'Cost', logistics: 'Speed vs cost', guardrail: 'Guardrail', scorecard: 'Priorities', unified: 'Scoring', defaults: 'Defaults', kci: 'Disruptions' }
const avg = (s) => (s.influence.delivery + s.influence.rto + s.influence.first_attempt + s.influence.time) / 4
const pct = (v) => (v >= 10 ? v.toFixed(0) : v >= 1 ? v.toFixed(1) : v.toFixed(2))
const reduceMotion = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

function StatusDot({ status, size = 10 }) {
  return <Box component='span' sx={{ display: 'inline-block', width: size, height: size, borderRadius: '50%', background: STATUS[status].color, flexShrink: 0 }} />
}

function SourceBadge({ source, small = false, prefix = '' }) {
  const m = SOURCE[source]
  if (!m) return null
  return (
    <Box component='span' title={m.tip} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: small ? 0.75 : 1, py: small ? 0.1 : 0.25, borderRadius: 99,
      fontSize: small ? 11 : 12, fontWeight: 600, color: textOn(m.color), background: `${m.color}1F`, border: `1px solid ${m.color}55`, whiteSpace: 'nowrap' }}>
      <Box component='span' sx={{ width: 6, height: 6, borderRadius: '50%', background: m.color }} />{prefix}{m.label}
    </Box>
  )
}

// ── how the ranking score is built: parts sized by weight, each with its source ──
function ScoreBuild({ parts, merchant }) {
  const shown = parts.filter((p) => p.weight > 0)
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', height: 34, borderRadius: 2, overflow: 'hidden', border: `1px solid ${C.line}` }}>
        {shown.map((p) => (
          <Box key={p.key} title={`${p.name}: ${(p.weight * 100).toFixed(0)}%`} sx={{ flex: `0 0 ${p.weight * 100}%`, background: PART_COLOR[p.key], color: onColor(PART_COLOR[p.key]), fontSize: 12, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', whiteSpace: 'nowrap', transition: 'flex-basis .8s cubic-bezier(.2,.8,.2,1)' }}>
            {p.weight >= 0.08 ? `${Math.round(p.weight * 100)}%` : ''}
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 1.5 }}>
        {parts.map((p) => (
          <Box key={p.key} sx={{ border: `1px solid ${C.line}`, borderRadius: 1.5, p: 1.75, display: 'flex', flexDirection: 'column', gap: 0.75, opacity: p.weight > 0 ? 1 : 0.5, boxShadow: `inset 0 3px 0 ${PART_COLOR[p.key]}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'baseline' }}>
              <b style={{ color: C.ink, fontSize: 14 }}>{p.name}</b>
              <Typography sx={{ fontWeight: 700, color: textOn(PART_COLOR[p.key]), fontVariantNumeric: 'tabular-nums' }}>{(p.weight * 100).toFixed(0)}%</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              <SourceBadge source={p.source} />
              {p.extra_source && <SourceBadge source={p.extra_source} prefix='+ ' />}
              {p.fallback_source && <SourceBadge source={p.fallback_source} prefix='new couriers: ' />}
            </Box>
            <Typography sx={{ fontSize: 12.5, color: C.muted }}>{p.weight > 0 ? p.note : 'Weight 0 with these settings: this part does not count.'}</Typography>
          </Box>
        ))}
      </Box>
      <Typography sx={{ fontSize: 12.5, color: C.muted }}>
        Widths are {merchant ? "this merchant's" : 'the global'} weights. Speed and cost share the logistics weight by the speed↔cost blend (α).
        The signals below are all known or measured; only the model's outputs are predicted.
      </Typography>
    </Box>
  )
}

// ── orbit: 8 families feeding PACE; circle size = influence ────────────────
function Orbit({ families, sel, onSel, statusByFam }) {
  const cx = 280, cy = 262, R = 196
  const nodes = families.map((f, i) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / families.length
    return { ...f, x: cx + R * Math.cos(a), y: cy + R * Math.sin(a), r: 20 + 5.2 * Math.sqrt(f.avg) }
  })
  const still = reduceMotion()
  return (
    <Box component='svg' viewBox='0 0 560 540' sx={{ width: '100%', maxWidth: 560, height: 'auto', display: 'block', mx: 'auto' }} role='img' aria-label='Signal families feeding PACE'>
      <circle cx={cx} cy={cy} r={R} fill='none' stroke={C.line} strokeDasharray='3 7' />
      {nodes.map((f, i) => {
        const on = i === sel
        const dim = statusByFam && !statusByFam[f.key]?.used && !statusByFam[f.key]?.fallback
        const k = Math.max(1, Math.round(f.avg / 6))
        return (
          <g key={`l${f.key}`} opacity={dim ? 0.3 : 1}>
            <path id={`sigp${i}`} d={`M${f.x} ${f.y} L${cx} ${cy}`} stroke={on ? f.color : C.line} strokeWidth={on ? 3 : 1.5} fill='none' />
            {!still && Array.from({ length: k }).map((_, j) => (
              <circle key={j} r={on ? 4 : 3} fill={f.color} opacity={on ? 1 : 0.55}>
                <animateMotion dur={`${2.2 + (i % 3) * 0.3}s`} repeatCount='indefinite' begin={`${(j / k) * 2.2}s`}><mpath href={`#sigp${i}`} /></animateMotion>
              </circle>
            ))}
          </g>
        )
      })}
      <circle cx={cx} cy={cy} r={66} fill={C.primary} opacity={0.12}>
        {!still && <animate attributeName='r' values='62;74;62' dur='2.6s' repeatCount='indefinite' />}
      </circle>
      <circle cx={cx} cy={cy} r={56} fill={C.primary} />
      <text x={cx} y={cy - 4} textAnchor='middle' fontSize='22' fontWeight='700' fill='#fff'>PACE</text>
      <text x={cx} y={cy + 18} textAnchor='middle' fontSize='12' fill='#fff' opacity='.9'>{families.reduce((a, f) => a + f.n, 0)} signals</text>
      {nodes.map((f, i) => {
        const on = i === sel
        const below = f.y >= cy - 5
        const st = statusByFam?.[f.key]
        const dim = statusByFam && !st?.used && !st?.fallback
        return (
          <g key={f.key} role='button' tabIndex={0} aria-pressed={on} aria-label={`${f.name}, ${f.n} signals`} style={{ cursor: 'pointer', outline: 'none' }}
            opacity={dim ? 0.4 : 1}
            onClick={() => onSel(i)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSel(i) } }}>
            {on && <circle cx={f.x} cy={f.y} r={f.r + 9} fill={f.color} opacity={0.18} />}
            <circle cx={f.x} cy={f.y} r={f.r} fill={f.color} stroke={on ? '#fff' : 'none'} strokeWidth={3} />
            <text x={f.x} y={f.y + 5} textAnchor='middle' fontSize='15' fontWeight='700' fill={onColor(f.color)}>{f.n}</text>
            <text x={f.x} y={below ? f.y + f.r + 18 : f.y - f.r - (st ? 24 : 9)} textAnchor='middle' fontSize='12.5' fontWeight='700' fill={C.ink}>{FAM[f.key].short}</text>
            {st && (
              <text x={f.x} y={below ? f.y + f.r + 33 : f.y - f.r - 9} textAnchor='middle' fontSize='11'>
                <tspan fill={C.success} fontWeight='700'>{st.used || 0} used</tspan>
                {st.fallback ? <tspan fill={palette.warning.dark} fontWeight='700'>{` · ${st.fallback} fb`}</tspan> : null}
              </text>
            )}
          </g>
        )
      })}
    </Box>
  )
}

function Bar({ value, color, max = 100 }) {
  const [w, setW] = useState(0)
  useEffect(() => { const id = requestAnimationFrame(() => setW(Math.min(100, (100 * value) / max))); return () => cancelAnimationFrame(id) }, [value, max])
  return (
    <Box sx={{ height: 10, borderRadius: 99, background: C.soft, border: `1px solid ${C.line}`, overflow: 'hidden' }}>
      <Box sx={{ height: '100%', width: `${w}%`, background: color, borderRadius: 99, transition: 'width .7s cubic-bezier(.2,.8,.2,1)' }} />
    </Box>
  )
}

function FamilyDetail({ fam, signals, merchantMode, hasMerchant }) {
  const infl = PRED.map((p) => ({ ...p, v: signals.reduce((a, s) => a + s.influence[p.k], 0) }))
  const counts = hasMerchant ? signals.reduce((a, s) => ({ ...a, [s.status]: (a[s.status] || 0) + 1 }), {}) : null
  const top = [...signals].sort((a, b) => avg(b) - avg(a)).slice(0, 6)
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'center' }}>
        <Box sx={{ width: 52, height: 52, borderRadius: 1.5, display: 'grid', placeItems: 'center', color: onColor(fam.color), fontWeight: 700, fontSize: 18, background: fam.color, flexShrink: 0 }}>{fam.n}</Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: C.ink }}>{fam.name}</Typography>
          <Typography sx={{ fontSize: 13.5, color: C.muted }}>{fam.n} signals · {fam.avg.toFixed(1)}% of PACE's prediction influence</Typography>
        </Box>
      </Box>
      <Typography sx={{ color: C.ink }}>{FAM_TEXT[fam.key]}</Typography>
      {counts && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {['used', 'fallback', 'unused'].map((k) => (
            <Box key={k} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 13 }}><StatusDot status={k} />{counts[k] || 0} {STATUS[k].label.toLowerCase()}</Box>
          ))}
        </Box>
      )}
      <Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.muted, mb: 1 }}>Most influential here</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {top.map((s) => (
            <Box key={s.key} sx={{ fontSize: 12.5, px: 1.25, py: 0.5, borderRadius: 99, border: `1px solid ${C.line}`, background: C.soft, display: 'inline-flex', gap: 0.75, alignItems: 'center' }}>
              {hasMerchant && <StatusDot status={s.status} size={8} />}{s.name}
            </Box>
          ))}
        </Box>
      </Box>
      <Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.muted, mb: 1.25 }}>Influence on each prediction</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {infl.map((p) => (
            <Box key={p.k} sx={{ display: 'grid', gridTemplateColumns: { xs: '104px minmax(0,1fr) 46px', sm: '150px minmax(0,1fr) 46px' }, gap: 1.25, alignItems: 'center', fontSize: 13.5 }}>
              <span>{p.name}</span><Bar value={p.v} color={fam.color} max={70} /><b style={{ textAlign: 'right', color: C.ink }}>{p.v.toFixed(1)}%</b>
            </Box>
          ))}
        </Box>
      </Box>
      {!merchantMode && <Typography sx={{ fontSize: 12, color: C.muted }}>Influence = share of what the prediction models learned from (gain).</Typography>}
    </Box>
  )
}

function Drivers({ families, signals, onSel }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      {PRED.map((p) => {
        const tot = signals.reduce((a, s) => a + s.influence[p.k], 0) || 1
        return (
          <Box key={p.k} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: '170px minmax(0,1fr)' }, gap: 1.5, alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 700, color: C.ink, fontSize: 14 }}>{p.name}</Typography>
            <Box sx={{ display: 'flex', height: 26, borderRadius: 2, overflow: 'hidden', border: `1px solid ${C.line}` }}>
              {families.map((f, i) => {
                const w = (100 * signals.filter((s) => s.family === f.key).reduce((a, s) => a + s.influence[p.k], 0)) / tot
                return (
                  <Box key={f.key} title={`${f.name}: ${w.toFixed(1)}%`} onClick={() => onSel(i)}
                    sx={{ flex: `0 0 ${w}%`, background: f.color, color: onColor(f.color), fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                    {w >= 9 ? `${Math.round(w)}%` : ''}
                  </Box>
                )
              })}
            </Box>
          </Box>
        )
      })}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', fontSize: 12.5 }}>
        {families.map((f) => (
          <Box key={f.key} component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}><Box component='i' sx={{ width: 10, height: 10, borderRadius: 0.75, background: f.color }} />{FAM[f.key].short}</Box>
        ))}
      </Box>
    </Box>
  )
}

// ── how a track-record signal is calculated (sample data) ──────────────────
function wilson(s, n, z = 1.959964) {
  const p = s / n, den = 1 + (z * z) / n, c = p + (z * z) / (2 * n), m = z * Math.sqrt((p * (1 - p) + (z * z) / (4 * n)) / n)
  return (c - m) / den
}
const DAYS = (() => {
  let seed = 7
  const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647
  return Array.from({ length: 120 }, (_, i) => {
    const n = 6 + Math.floor(rnd() * 10)
    let r = Math.floor(n * (0.06 + rnd() * 0.12))
    if (i > 70 && i < 78) r = Math.min(n, r + 3)
    return { n, d: n - r, r }
  })
})()

function WindowDemo() {
  const [end, setEnd] = useState(95)
  useEffect(() => {
    if (reduceMotion()) return undefined
    const id = setInterval(() => setEnd((e) => (e >= DAYS.length - 1 ? 95 : e + 1)), 900)
    return () => clearInterval(id)
  }, [])
  const W = 620, x0 = 10, cw = (W - 20) / DAYS.length, base = 120, s = end - 90
  let n = 0, d = 0, r = 0
  for (let i = s; i < end; i++) { n += DAYS[i].n; d += DAYS[i].d; r += DAYS[i].r }
  const ox = x0 + end * cw + cw / 2
  return (
    <Box>
      <Box component='svg' viewBox='0 0 620 170' sx={{ width: '100%', height: 'auto', display: 'block' }} role='img' aria-label='A 90-day window of past shipments ending the day before the order'>
        {DAYS.map((dd, i) => {
          const h = dd.n * 5
          return (
            <g key={i}>
              <rect x={x0 + i * cw} y={base - h} width={cw - 1} height={(h * dd.d) / dd.n} fill={C.success} opacity='.85' />
              <rect x={x0 + i * cw} y={base - (h * dd.r) / dd.n} width={cw - 1} height={(h * dd.r) / dd.n} fill={C.error} opacity='.85' />
            </g>
          )
        })}
        <line x1={x0} x2={W - 10} y1={base + 0.5} y2={base + 0.5} stroke={C.line} />
        <rect x={x0 + s * cw - 1} y={18} width={90 * cw + 1} height={base - 12} rx='6' fill={C.primary} opacity='.1' stroke={C.primary} strokeWidth='1.5' style={{ transition: 'x .4s' }} />
        <text x={x0 + s * cw + 6} y={12} fontSize='11.5' fontWeight='700' fill={C.primary}>90-day window, up to the day before</text>
        <line x1={ox} x2={ox} y1={14} y2={base + 6} stroke={C.ink} strokeWidth='2' strokeDasharray='4 3' />
        <text x={Math.min(ox, W - 40)} y={148} textAnchor='middle' fontSize='12' fontWeight='700' fill={C.ink}>Order day</text>
        <text x={x0} y={166} fontSize='11' fill={C.muted}>Each bar = one day of finished shipments · green delivered · red returned</text>
      </Box>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 1 }}>
        {[[n.toLocaleString('en-IN'), 'finished shipments in window'], [`${((100 * d) / n).toFixed(1)}%`, 'delivered'], [`${((100 * r) / n).toFixed(1)}%`, 'returned (RTO)']].map(([v, t]) => (
          <Box key={t}><Typography sx={{ fontSize: 22, fontWeight: 700, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>{v}</Typography><Typography sx={{ fontSize: 12.5, color: C.muted }}>{t}</Typography></Box>
        ))}
      </Box>
      <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 1 }}>
        Each day PACE recounts a courier's shipments that reached a final status in the last 7, 30 and 90 days, stopping at the day before the order. Nothing from the order's own day or later is used. Illustration with sample data.
      </Typography>
    </Box>
  )
}

function WilsonCard({ name, s, n, color }) {
  const raw = s / n, lo = wilson(s, n)
  return (
    <Box sx={{ border: `1px solid ${C.line}`, borderRadius: 1.5, p: 1.75, background: C.soft, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <b style={{ color: C.ink }}>{name}</b><Typography sx={{ fontSize: 12.5, color: C.muted }}>{s.toLocaleString('en-IN')} of {n.toLocaleString('en-IN')} delivered</Typography>
      </Box>
      {[['Raw rate', raw, 0.45], ['Cautious', lo, 1]].map(([t, v, o]) => (
        <Box key={t} sx={{ display: 'grid', gridTemplateColumns: '86px minmax(0,1fr) 44px', gap: 1, alignItems: 'center', fontSize: 13 }}>
          <span>{t}</span>
          <Box sx={{ height: 10, borderRadius: 99, background: '#fff', border: `1px solid ${C.line}`, overflow: 'hidden' }}><Box sx={{ height: '100%', width: `${v * 100}%`, background: color, opacity: o }} /></Box>
          <b style={{ textAlign: 'right', color: C.ink }}>{Math.round(v * 100)}%</b>
        </Box>
      ))}
    </Box>
  )
}

// ── the full list ───────────────────────────────────────────────────────────
function SignalTable({ signals, families, hasMerchant, merchantMode }) {
  const [q, setQ] = useState('')
  const [fam, setFam] = useState('all')
  const [st, setSt] = useState('all')
  const [src, setSrc] = useState('all')
  const [sort, setSort] = useState('infl')
  const maxAvg = Math.max(...signals.map(avg), 1)
  const order = families.map((f) => f.key)
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase()
    const r = signals.filter((s) => (fam === 'all' || s.family === fam) && (st === 'all' || s.status === st) && (src === 'all' || s.source === src)
      && (!t || `${s.name} ${merchantMode ? '' : s.key} ${s.how} ${FAM[s.family].short}`.toLowerCase().includes(t)))
    const rank = { used: 0, fallback: 1, unused: 2 }
    return r.sort(sort === 'name' ? (a, b) => a.name.localeCompare(b.name)
      : sort === 'fam' ? (a, b) => order.indexOf(a.family) - order.indexOf(b.family) || avg(b) - avg(a)
        : sort === 'status' ? (a, b) => rank[a.status] - rank[b.status] || avg(b) - avg(a)
          : (a, b) => avg(b) - avg(a))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signals, q, fam, st, src, sort, merchantMode])
  const chip = (on) => ({
    fontSize: 12.5, px: 1.25, py: 0.6, borderRadius: 99, cursor: 'pointer', border: `1px solid ${on ? C.ink : C.line}`,
    background: on ? C.ink : '#fff', color: on ? '#fff' : C.ink, display: 'inline-flex', alignItems: 'center', gap: 0.75, userSelect: 'none',
  })
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
        <TextField size='small' value={q} onChange={(e) => setQ(e.target.value)} placeholder='Search signals, e.g. COD, drop city, transit'
          sx={{ flex: '1 1 260px', minWidth: 0 }} InputProps={{ startAdornment: <InputAdornment position='start'><SearchRoundedIcon fontSize='small' /></InputAdornment> }} />
        <TextField select size='small' label='Sort' value={sort} onChange={(e) => setSort(e.target.value)} sx={{ minWidth: 200 }}>
          <MenuItem value='infl'>Most influential first</MenuItem>
          {hasMerchant && <MenuItem value='status'>Used first</MenuItem>}
          <MenuItem value='fam'>By family</MenuItem>
          <MenuItem value='name'>A to Z</MenuItem>
        </TextField>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }} role='group' aria-label='Filter by family'>
        <Box role='button' tabIndex={0} sx={chip(fam === 'all')} onClick={() => setFam('all')}>All · {signals.length}</Box>
        {families.map((f) => (
          <Box key={f.key} role='button' tabIndex={0} sx={chip(fam === f.key)} onClick={() => setFam(f.key)}>
            <Box component='i' sx={{ width: 10, height: 10, borderRadius: 0.75, background: f.color }} />{FAM[f.key].short} · {f.n}
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }} role='group' aria-label='Filter by source'>
        <Box role='button' tabIndex={0} sx={chip(src === 'all')} onClick={() => setSrc('all')}>Any source</Box>
        {['known', 'measured'].map((k) => (
          <Box key={k} role='button' tabIndex={0} sx={chip(src === k)} onClick={() => setSrc(k)} title={SOURCE[k].tip}>
            <Box component='span' sx={{ width: 8, height: 8, borderRadius: '50%', background: SOURCE[k].color }} />{SOURCE[k].label} · {signals.filter((s) => s.source === k).length}
          </Box>
        ))}
      </Box>
      {hasMerchant && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }} role='group' aria-label='Filter by use'>
          <Box role='button' tabIndex={0} sx={chip(st === 'all')} onClick={() => setSt('all')}>Any use</Box>
          {Object.keys(STATUS).map((k) => (
            <Box key={k} role='button' tabIndex={0} sx={chip(st === k)} onClick={() => setSt(k)}>
              <StatusDot status={k} />{STATUS[k].label} · {signals.filter((s) => s.status === k).length}
            </Box>
          ))}
        </Box>
      )}
      <Typography sx={{ fontSize: 12.5, color: C.muted }}>Showing {rows.length} of {signals.length} signals</Typography>
      <Box sx={{ overflow: 'auto', maxHeight: 620, border: `1px solid ${C.line}`, borderRadius: 1.5 }}>
        <Box component='table' sx={{ borderCollapse: 'collapse', width: '100%', minWidth: 860, fontSize: 13.5,
          '& th': { position: 'sticky', top: 0, background: C.soft, textAlign: 'left', fontSize: 12, letterSpacing: 0.6, textTransform: 'uppercase', color: C.muted, p: '10px 12px', borderBottom: `1px solid ${C.line}`, zIndex: 1 },
          '& td': { p: '10px 12px', borderBottom: `1px solid ${C.line}`, verticalAlign: 'top' } }}>
          <thead>
            <tr>
              <th>#</th><th>Signal</th><th>Family</th><th>Source</th><th>How it is calculated</th>
              {hasMerchant && <th>Use for this merchant</th>}
              {!merchantMode && <th>Influence %<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 34px)', gap: 0.75, mt: 0.5 }}>{PRED.map((p) => <span key={p.k}>{p.abbr}</span>)}</Box></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((s, i) => (
              <Box component='tr' key={s.key} sx={hasMerchant ? { background: `${STATUS[s.status].color}0F`, boxShadow: `inset 3px 0 0 ${STATUS[s.status].color}` } : undefined}>
                <td style={{ color: C.muted, width: 42, fontVariantNumeric: 'tabular-nums' }}>{i + 1}</td>
                <td>
                  <Typography sx={{ fontWeight: 600, color: C.ink, fontSize: 13.5 }}>{s.name}</Typography>
                  {!merchantMode && <Typography component='code' sx={{ fontSize: 11.5, color: C.muted }}>{s.key}</Typography>}
                </td>
                <td><Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12, whiteSpace: 'nowrap' }}><Box component='i' sx={{ width: 10, height: 10, borderRadius: 0.75, background: FAM[s.family].color }} />{FAM[s.family].short}</Box></td>
                <td><SourceBadge source={s.source} small /></td>
                <td>{s.how}</td>
                {hasMerchant && (
                  <td style={{ minWidth: 210 }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontWeight: 700, color: s.status === 'unused' ? C.muted : C.ink }}><StatusDot status={s.status} />{STATUS[s.status].label}</Box>
                    {s.via?.length > 0 && <Typography sx={{ fontSize: 12, color: C.muted, mt: 0.5 }}>{s.via.join(' · ')}</Typography>}
                  </td>
                )}
                {!merchantMode && (
                  <td>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 34px)', gap: 0.75, fontSize: 11.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {PRED.map((p) => <b key={p.k} style={{ fontWeight: 600, color: C.ink }}>{pct(s.influence[p.k])}</b>)}
                    </Box>
                    <Box sx={{ height: 6, background: C.soft, borderRadius: 99, overflow: 'hidden', mt: 0.75, border: `1px solid ${C.line}` }}><Box sx={{ height: '100%', width: `${(100 * avg(s)) / maxAvg}%`, background: C.primary }} /></Box>
                  </td>
                )}
              </Box>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} style={{ color: C.muted }}>No signal matches "{q}". Try a shorter word, like "COD" or "city".</td></tr>}
          </tbody>
        </Box>
      </Box>
    </Box>
  )
}

function DecisionInputs({ data }) {
  const m = data.merchant
  const knobs = m ? m.knobs : data.global_knobs
  const STATIC = [
    ['scorecard', 'Lane scorecard', 'Recent delivered %, FASR and on-time first attempt on this lane, from the finest of courier × pickup area → drop area, drop area, drop state or courier with at least 30 shipments.'],
    ['quote', 'Live price quote', "The courier's rate-card price for this parcel and lane, sent by proship."],
    ['wd', 'Expected weight-dispute charge', "The courier's dispute rate × its average extra charge."],
    ['eta', 'Predicted delivery time', 'From the typical delivery-time model; sets the speed side of speed vs cost.'],
    ['kci', 'Live disruptions (KCI)', 'Active courier disruption events on the pickup or drop area, which can push the ETA.'],
    ['guardrail', 'Minimum delivery guardrail', 'Couriers below the minimum expected delivery are not ranked.'],
  ]
  const SRC_OF = { scorecard: 'measured', quote: 'known', wd: 'measured', eta: 'predicted', kci: 'known', guardrail: 'measured' }
  const byKey = Object.fromEntries((m?.inputs || []).map((x) => [x.key, x]))
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 1.5 }}>
        {STATIC.map(([k, name, desc]) => {
          const x = byKey[k]
          return (
            <Box key={k} sx={{ border: `1px solid ${x ? `${STATUS[x.status].color}66` : C.line}`, borderRadius: 1.5, p: 1.75, background: x ? `${STATUS[x.status].color}0F` : C.soft, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>{x && <StatusDot status={x.status} />}<b style={{ color: C.ink }}>{name}</b><SourceBadge source={SRC_OF[k]} small /></Box>
              <Typography sx={{ fontSize: 12.5, color: C.muted }}>{x ? x.reason : desc}</Typography>
            </Box>
          )
        })}
      </Box>
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.muted }}>
        Business settings ({data.knob_catalog.length}) · {m ? 'values in effect for this merchant' : 'global values'}
      </Typography>
      <Box sx={{ overflowX: 'auto', border: `1px solid ${C.line}`, borderRadius: 1.5 }}>
        <Box component='table' sx={{ borderCollapse: 'collapse', width: '100%', minWidth: 680, fontSize: 13.5,
          '& th': { background: C.soft, textAlign: 'left', fontSize: 12, letterSpacing: 0.6, textTransform: 'uppercase', color: C.muted, p: '10px 12px', borderBottom: `1px solid ${C.line}` },
          '& td': { p: '10px 12px', borderBottom: `1px solid ${C.line}`, verticalAlign: 'top' } }}>
          <thead><tr><th>Setting</th><th>Group</th><th>Value</th><th>Default</th><th>What it does</th></tr></thead>
          <tbody>
            {data.knob_catalog.map((k) => {
              const v = knobs?.[k.key]
              const changed = v !== undefined && v !== null && Math.abs(v - k.default) > 1e-9
              return (
                <tr key={k.key}>
                  <td><Typography sx={{ fontWeight: 600, color: C.ink, fontSize: 13.5 }}>{k.label}</Typography></td>
                  <td><Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: textOn(C.violet) }}>{KNOB_GROUP[k.group] || k.group}</Typography></td>
                  <td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: changed ? 700 : 400, color: changed ? C.primary : C.ink }}>{v === undefined || v === null ? '—' : +(+v).toFixed(4)}</td>
                  <td style={{ fontVariantNumeric: 'tabular-nums', color: C.muted }}>{+(+k.default).toFixed(4)}</td>
                  <td>{k.description}</td>
                </tr>
              )
            })}
          </tbody>
        </Box>
      </Box>
    </Box>
  )
}

/**
 * props:
 *  - load(merchantId, paymentMode) → Promise<PACE /admin/signals body>
 *  - merchantPicker: admin only — ({ value, onChange }) => element; omit in the merchant panel
 *  - merchantMode: merchant panel (own merchant always applied, technical names hidden)
 */
export default function SignalsTab({ load, MerchantPicker, merchantMode = false }) {
  const [merchant, setMerchant] = useState(null)
  const [pm, setPm] = useState('')
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(true)
  const [sel, setSel] = useState(0)
  const merchantId = merchant?.merchantId

  useEffect(() => {
    let live = true
    setBusy(true)
    setErr(null)
    load(merchantMode ? null : merchantId, pm || null)
      .then((d) => { if (live) setData(d) })
      .catch(() => { if (live) setErr('Could not load the signal list from PACE. Try again in a minute.') })
      .finally(() => { if (live) setBusy(false) })
    return () => { live = false }
  }, [load, merchantId, pm, merchantMode])

  const hasMerchant = !!data?.merchant
  const families = useMemo(() => {
    if (!data) return []
    return data.families.map((f) => {
      const sig = data.signals.filter((s) => s.family === f.key)
      return { ...f, color: FAM[f.key].color, avg: sig.reduce((a, s) => a + avg(s), 0) }
    })
  }, [data])
  const statusByFam = useMemo(() => {
    if (!hasMerchant) return null
    const out = {}
    data.signals.forEach((s) => { out[s.family] = out[s.family] || {}; out[s.family][s.status] = (out[s.family][s.status] || 0) + 1 })
    return out
  }, [data, hasMerchant])

  if (!data) {
    return busy ? <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box> : <Alert severity='error'>{err}</Alert>
  }
  const m = data.merchant
  const fb = m?.fallback?.share
  const fam = families[sel]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <SectionHead kicker='What PACE reads' color={C.violet}
        title={`${data.n_signals} signals for every courier, on every order`}
        sub={`PACE reads ${data.n_signals} signals in ${data.families.length} families to predict how each courier will do. Every one is listed below, straight from the model running today${merchantMode ? '' : ` (${data.model_run})`}. ${merchantMode ? 'Colours show which ones your settings use for ranking.' : 'Pick a merchant to see which ones its settings use for ranking.'}`} />

      <Card hover={false} sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {!merchantMode && MerchantPicker && (
            <Box sx={{ flex: '1 1 320px', minWidth: 0 }}><MerchantPicker value={merchant} onChange={setMerchant} /></Box>
          )}
          <ToggleButtonGroup size='small' exclusive value={pm} onChange={(_e, v) => setPm(v ?? '')} aria-label='Payment mode'>
            <ToggleButton value=''>All orders</ToggleButton>
            <ToggleButton value='COD'>COD</ToggleButton>
            <ToggleButton value='PREPAID'>Prepaid</ToggleButton>
          </ToggleButtonGroup>
          {busy && <CircularProgress size={20} />}
        </Box>
        {err && <Alert severity='error'>{err}</Alert>}
        {!m && !merchantMode && (
          <Typography sx={{ color: C.muted, fontSize: 14 }}>No merchant selected: showing every signal PACE reads. Pick a merchant to colour them by how its settings use them.</Typography>
        )}
        {m && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {['used', 'fallback', 'unused'].map((k) => (
                <Box key={k} sx={{ flex: '1 1 180px', borderRadius: 1.5, p: 1.75, border: `1px solid ${STATUS[k].color}66`, background: `${STATUS[k].color}12` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><StatusDot status={k} size={12} /><Typography sx={{ fontWeight: 700, color: C.ink, fontSize: 14 }}>{STATUS[k].label}</Typography></Box>
                  <Typography sx={{ fontSize: 30, fontWeight: 700, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>{m.counts[k]}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: C.muted }}>
                    {k === 'used' ? 'signals that change this ranking'
                      : k === 'fallback' ? `only for couriers new to a lane${fb != null ? ` (${(fb * 100).toFixed(1)}% of recent rankings)` : ''}`
                        : 'read, but these settings give them no say'}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box component='ul' sx={{ m: 0, pl: 2.5, color: C.ink, fontSize: 13.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {['time', 'delivery', 'first_attempt', 'rto'].map((k) => <li key={k}>{m.predictions[k].reason}</li>)}
              <li>{m.direct.courier_price.reason}</li>
            </Box>
          </Box>
        )}
      </Card>

      {(m?.score_parts || data.score_parts) && (
        <Card hover={false} sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <SectionHead kicker='How the score is built' color={C.success} title='What is known, what is measured, what is predicted'
            sub='Each courier gets one score from these parts. The badges say where each number comes from.' />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {Object.entries(SOURCE).map(([k, v]) => (
              <Box key={k} sx={{ display: 'flex', gap: 1, alignItems: 'center', fontSize: 13 }}><SourceBadge source={k} /><span style={{ color: C.muted }}>{v.tip}</span></Box>
            ))}
          </Box>
          <ScoreBuild parts={m?.score_parts || data.score_parts} merchant={!!m} />
        </Card>
      )}

      <Reveal>
        <Card hover={false} sx={{ p: { xs: 2, md: 3 }, display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1.05fr) minmax(0,1fr)' }, gap: 3, alignItems: 'start' }}>
          <Box>
            <Orbit families={families} sel={sel} onSel={setSel} statusByFam={statusByFam} />
            <Typography sx={{ fontSize: 12.5, color: C.muted, textAlign: 'center', mt: 0.5 }}>Bigger circle = more influence on PACE's predictions. Click a family.</Typography>
          </Box>
          {fam && <FamilyDetail fam={fam} signals={data.signals.filter((s) => s.family === fam.key)} merchantMode={merchantMode} hasMerchant={hasMerchant} />}
        </Card>
      </Reveal>

      <Reveal>
        <Card hover={false} sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <SectionHead kicker='What drives each prediction' color={C.primary} title='Different questions lean on different signals'
            sub={'Courier track record decides most of "will it deliver?" and "will it come back?". The route and the courier\'s service type decide most of "how long will it take?".'} />
          <Drivers families={families} signals={data.signals} onSel={setSel} />
        </Card>
      </Reveal>

      <Reveal>
        <Card hover={false} sx={{ p: { xs: 2, md: 3 }, display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1.4fr) minmax(0,1fr)' }, gap: 3 }}>
          <Box>
            <SectionHead kicker='How a track-record signal is calculated' color={C.teal} title='Only finished shipments, only from the past' />
            <WindowDemo />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.muted }}>Small numbers get a cautious score</Typography>
            <WilsonCard name='Courier A, new on this lane' s={9} n={10} color={C.warning} />
            <WilsonCard name='Courier B, proven on this lane' s={870} n={1000} color={C.success} />
            <Typography sx={{ fontSize: 12.5, color: C.muted }}>A raw 90% from 10 shipments could be luck. PACE also keeps a cautious version of each rate (the lower end of what the data supports), so a courier can't outrank a proven one on a handful of lucky deliveries.</Typography>
          </Box>
        </Card>
      </Reveal>

      <Card hover={false} sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <SectionHead kicker='The full list' color={C.violet} title={`All ${data.n_signals} signals, one by one`}
          sub={`The exact input list of the model running today.${hasMerchant ? ' Coloured by how this merchant\'s settings use each one.' : ''}${merchantMode ? '' : ' Influence columns: Del = will it deliver, RTO = will it come back, 1st = first attempt on time, Time = how long.'}`} />
        <SignalTable signals={data.signals} families={families} hasMerchant={hasMerchant} merchantMode={merchantMode} />
      </Card>

      <Card hover={false} sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <SectionHead kicker='After the predictions' color={C.success} title='What the final ranking uses'
          sub='The signals feed the predictions. The ranking step combines them with live inputs and the business settings below.' />
        <DecisionInputs data={data} />
      </Card>
    </Box>
  )
}
